"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import SellerCard from "@/app/components/common/basket/SellerCard";
import BasketItem from "@/app/components/common/basket/BasketItem";
import OrderSummary from "@/app/components/common/basket/OrderSummary";
import ProductSection from "@/app/components/common/sections/ProductSection";
import EmptyBasket from "@/app/components/common/basket/EmptyBasket";
import BasketBottomBar from "@/app/components/common/basket/BasketBottomBar";
import useCart from "@/app/lib/features/cart/cart.hook";
import useAuth from "@/app/lib/features/auth/auth.hook";
import Link from "next/link";
import useAlertDialog from "@/app/lib/features/alertDialog/alertDialog.hook";
import MyBasketPageSkeleton from "@/app/components/elements/skeletons/MyBasketPageSkeleton";
import { getCartItemName, parsePrice } from "@/app/utils/cart.utils";
import { trackViewCart } from "@/app/lib/tracking/events";
import AuthRequired from "@/app/components/common/basket/AuthRequired";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const MyBasketPage = () => {
  const router = useRouter();
  const [authOpen, setAuthOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const {
    cart,
    isLoading,
    isSuccess,
    itemUpdating,
    itemToggling,
    isTogglingAll,
    sellerToggling,
    fetchCart,
    updateQuantity,
    removeItem,
    toggleItem,
    toggleAll,
    toggleSeller,
  } = useCart();

  const { showAlertDialog } = useAlertDialog();

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    document.body.classList.add("has-bottom-bar");
    return () => document.body.classList.remove("has-bottom-bar");
  }, []);

  // view_cart — sayfa yüklemesine değil, sepet ekranının gerçekten
  // görüntülenmesine bağlı. Mount anında cart henüz null olduğu için burada
  // veri hazır olana kadar bekliyoruz; "Sepete Git" gibi client-side
  // navigasyonlarda page load olmadığından GTM sayfa tetikleyicisi çalışmıyordu.
  // Ref bu görüntüleme boyunca tekrar göndermeyi engeller (sepet güncellemeleri
  // yeni bir "görüntüleme" değil); sayfadan çıkıp dönünce component remount
  // olacağı için ref sıfırlanır ve event yeniden gönderilir.
  const viewCartTrackedRef = useRef(false);

  useEffect(() => {
    if (viewCartTrackedRef.current) return;

    const items =
      cart?.grouped_items?.flatMap((group) =>
        group.items.map((item) => ({ item, sellerName: group.seller?.name })),
      ) ?? [];
    if (items.length === 0) return;

    viewCartTrackedRef.current = true;

    const trackedItems = items.map(({ item, sellerName }) => ({
      item_id: item.product_id,
      item_name: getCartItemName(item),
      item_brand: item.brand_name,
      affiliation: sellerName,
      price: parsePrice(item.price),
      quantity: item.quantity,
    }));

    trackViewCart({
      // value = kalemlerin toplamı, yani kargo hariç ürün tutarı.
      // cart_box'taki "Toplam" satırı kargoyu içeriyor; begin_checkout ile
      // aynı kuralı uyguluyoruz — kargo value'ya girmez.
      value: trackedItems.reduce(
        (sum, item) => sum + item.price * (item.quantity ?? 1),
        0,
      ),
      items: trackedItems,
    });
  }, [cart]);

  const handleQuantityChange = async (
    itemId: number,
    type: "increase" | "decrease",
  ) => {
    const item = cart?.grouped_items
      .flatMap((g) => g.items)
      .find((i) => i.id === itemId);

    if (!item) return;

    const newQuantity =
      type === "increase" ? item.quantity + 1 : item.quantity - 1;

    if (newQuantity < 1) return;

    const result = await updateQuantity(itemId, { quantity: newQuantity });

    if (result.success) {
      await fetchCart();
    } else {
      toast.error(result.message ?? "Miktar güncellenirken bir hata oluştu");
    }
  };

  const handleRemoveItem = (itemId: number) => {
    const group = cart?.grouped_items.find((g) =>
      g.items.some((i) => i.id === itemId),
    );
    const item = group?.items.find((i) => i.id === itemId);

    const itemName = item ? getCartItemName(item) : "Bu ürün";

    showAlertDialog({
      title: "Ürünü Sepetten Çıkar",
      description: `${itemName} sepetinizden çıkarılacak. Emin misiniz?`,
      cancelLabel: "İptal",
      action: {
        label: "Çıkar",
        loadingLabel: "Çıkarılıyor...",
        variant: "destructive",
        onClick: async () => {
          // Tracking verisi silmeden ÖNCE okunmalı — silme sonrası cart
          // yenilenince kalem listede olmuyor.
          const success = await removeItem(
            itemId,
            item
              ? {
                  itemId: item.product_id,
                  name: itemName,
                  brand: item.brand_name,
                  affiliation: group?.seller?.name,
                  price: parsePrice(item.price),
                  quantity: item.quantity,
                }
              : undefined,
          );
          if (!success) {
            toast.error("Ürün çıkarılamadı");
            throw new Error("Remove failed");
          }
          await fetchCart();
          toast.success("Ürün sepetten çıkarıldı");
        },
      },
    });
  };

  const handleToggleItem = async (itemId: number) => {
    const success = await toggleItem(itemId);

    if (success) {
      await fetchCart();
    } else {
      toast.error("Seçim değiştirilemedi");
    }
  };

  const handleToggleAll = async (isSelected: boolean) => {
    const success = await toggleAll({ is_selected: isSelected });

    if (success) {
      await fetchCart();
      toast.success(
        isSelected ? "Tüm ürünler seçildi" : "Tüm seçimler kaldırıldı",
      );
    } else {
      toast.error("İşlem başarısız oldu");
    }
  };

  const handleToggleSeller = async (sellerId: number, isSelected: boolean) => {
    const success = await toggleSeller({
      seller_id: sellerId,
      is_selected: isSelected,
    });

    if (success) {
      await fetchCart();
      toast.success(
        isSelected
          ? "Satıcının tüm ürünleri seçildi"
          : "Satıcının seçimleri kaldırıldı",
      );
    } else {
      toast.error("İşlem başarısız oldu");
    }
  };

  if (isLoading || (!cart && !isSuccess)) {
    return <MyBasketPageSkeleton />;
  }

  if (!cart || !cart.grouped_items || cart.grouped_items.length === 0) {
    return <EmptyBasket />;
  }

  const allItemsSelected = cart.grouped_items.every((group) =>
    group.items.every((item) => item.is_selected),
  );

  return (
    <section className="container-minify py-4 lg:py-8">
      {!isAuthenticated && (
        <div className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-4">
          <span className="text-sm text-gray-600">
            Siparişlerinizi takip etmek için giriş yapın.
          </span>
          {/* redirect şart: LoginForm parametreyi bulamazsa router.replace("/")
              çalışıyor ve kullanıcı sepetten ana sayfaya düşüyor. */}
          <Link
            href="/auth/login?redirect=%2Fsepet"
            onClick={() => trackGeneric("cart_login", "click")}
            className="shrink-0 text-sm font-medium text-main hover:underline"
          >
            Giriş Yap
          </Link>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 lg:mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => router.back()}
            className="cursor-pointer p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-600"
          >
            <ChevronLeft size={22} />
          </button>
          <h1 className="text-xl lg:text-3xl font-semibold text-gray-900">
            Sepetim
          </h1>
        </div>
        <span className="text-xs lg:text-sm text-gray-500 bg-gray-100 px-2 lg:px-3 py-1 rounded-full">
          {cart.cart_info.total_quantity} Ürün
        </span>
      </div>

      <div className="lg:hidden mb-4">
        <OrderSummary
          cartBox={cart.cart_box}
          summaryMessages={cart.summary_messages}
          discountInputText={cart.discount_input_text}
          couponsInfo={cart.cuppons}
          appliedDiscounts={cart.applied_discounts}
          continueShoppingUrl="/"
          discountOnly
          flat
          hideButton
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
        <div className="lg:col-span-8 space-y-4 lg:space-y-6">
          {cart.grouped_items.map((group, groupIndex) => {
            const groupAllSelected = group.items.every(
              (item) => item.is_selected,
            );

            return (
              <SellerCard
                key={group.seller.id || `guest-${groupIndex}`}
                sellerId={group.seller.id}
                name={group.seller.name}
                rating={group.seller.rating}
                sellerUrl={`/magaza/${group.seller.slug}`}
                banners={group.banners}
                freeShippingInfo={group.free_shipping_info}
                campaigns={group.campaigns}
                allSelected={groupAllSelected}
                onToggleAll={() =>
                  cart.grouped_items.length === 1
                    ? handleToggleAll(!allItemsSelected)
                    : handleToggleSeller(group.seller.id, !groupAllSelected)
                }
                isTogglingAll={
                  cart.grouped_items.length === 1
                    ? isTogglingAll
                    : sellerToggling === group.seller.id
                }
              >
                {group.items.map((item) => (
                  <BasketItem
                    key={`${item.id}-${item.product_id}`}
                    item={item}
                    isCheckboxLoading={itemToggling === item.id}
                    isQuantityUpdating={itemUpdating === item.id}
                    onQuantityChange={(type) =>
                      handleQuantityChange(item.id, type)
                    }
                    onRemove={() => handleRemoveItem(item.id)}
                    onToggleSelect={() => handleToggleItem(item.id)}
                  />
                ))}
              </SellerCard>
            );
          })}

          {cart.recommended_products &&
            cart.recommended_products.length > 0 && (
              <div className="hidden lg:block">
                <ProductSection
                  title={
                    cart.recommended_product_title || "Sepetine Uygun Ürünler"
                  }
                  products={cart.recommended_products}
                  showViewAll={false}
                  maxSlidesPerView={4}
                />
              </div>
            )}
        </div>

        <div className="hidden lg:block lg:col-span-4">
          <div className="lg:sticky lg:top-36">
          <OrderSummary
            cartBox={cart.cart_box}
            summaryMessages={cart.summary_messages}
            discountInputText={cart.discount_input_text}
            couponsInfo={cart.cuppons}
            appliedDiscounts={cart.applied_discounts}
            continueShoppingUrl="/"
            onCheckoutClick={() => {
              trackGeneric("cart_checkout", "checkout");
              if (isAuthenticated) {
                router.push("/account/checkout");
              } else {
                setAuthOpen(true);
              }
            }}
          />
          </div>
        </div>
      </div>

      {cart.recommended_products && cart.recommended_products.length > 0 && (
        <div className="lg:hidden mt-6">
          <ProductSection
            title={cart.recommended_product_title || "Sepetine Uygun Ürünler"}
            products={cart.recommended_products}
            showViewAll={false}
            maxSlidesPerView={4}
          />
        </div>
      )}
      <BasketBottomBar
        cartBox={cart.cart_box}
        summaryMessages={cart.summary_messages}
        discountInputText={cart.discount_input_text}
        couponsInfo={cart.cuppons}
        appliedDiscounts={cart.applied_discounts}
      />
      <AuthRequired open={authOpen} onClose={() => setAuthOpen(false)} />
    </section>
  );
};

export default MyBasketPage;
